/**
 * Flags — everything the console raises on its own.
 *
 * Three severities, and membership is decided by one question: is money unaccounted for? Only S1 pages
 * anyone. If S2 or S3 ever start paging, the console gets ignored — which is the real failure mode of
 * alerting, and it is a design decision rather than a config one.
 *
 * Every flag names the money at stake and the action that clears it. A flag with no next step is an
 * anxiety generator.
 */
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useLoader } from "../lib/session";
import { money, relative } from "../lib/format";
import { C, card, CardHead, EmptyState, ErrorState, Sev, Skeleton } from "../ui/primitives";
import { targetPath } from "./Overview";

export function Flags() {
  const { data, error, loading, reload } = useLoader(() => api.flags(), []);
  const flags = data?.flags ?? [];

  return (
    <>
      <div style={{ ...card, padding: "15px 17px", display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ fontWeight: 800, fontSize: 15 }}>Severity — three levels, deliberately</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 10 }}>
          <SevCard level={1} title="S1 — Pages a human, any hour"
            body="Money may be unaccounted for. The identity failing, a reconciliation break, a wallet negative that nobody authorised." />
          <SevCard level={2} title="S2 — Act this working day"
            body="Money is safe but stuck or unverified. Queues, stuck payouts, KYB holds, unverified tie-outs." />
          <SevCard level={3} title="S3 — Watch, no page"
            body="Deliberate decisions kept visible: overdrafts enabled, unusual sizes, recent fee changes." />
        </div>
      </div>

      <div style={card}>
        <CardHead title={`All flags${flags.length ? ` · ${flags.length} open` : ""}`}
          sub="Everything the console raises on its own. Each names the money at stake and the action that clears it." />
        {loading ? <Skeleton rows={5} label="Checking every rule…" />
          : error ? <ErrorState title="Couldn't load flags." body={error} onRetry={reload} />
          : flags.length === 0 ? (
            <EmptyState tone="good" title="No flags open."
              body="Every naira is accounted for, nothing is stuck, and no deliberate exceptions are in force. The console keeps checking — this page updates itself, you don't need to." />
          ) : (
            flags.map((f) => (
              <div key={f.id} style={{ display: "grid", gridTemplateColumns: "30px 1fr 130px 170px", gap: 10, padding: "12px 16px", borderBottom: "1px solid var(--line)", alignItems: "center" }}>
                <Sev level={f.severity} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 800 }}>{f.title}</div>
                  <div style={{ fontSize: 11.5, color: "var(--ink2)", lineHeight: 1.45, marginTop: 1 }}>{f.statement}</div>
                  <div style={{ fontSize: 10.5, color: "var(--muted)", fontWeight: 600, marginTop: 2 }}>raised {relative(f.raisedAt)}</div>
                </div>
                <span className="bp-mono" style={{ fontSize: 12, fontWeight: 700, color: "var(--ink2)" }}>{f.money === null ? "—" : money(f.money)}</span>
                <Link to={targetPath(f)} style={{ fontSize: 12, fontWeight: 800, textAlign: "right" }}>{f.action} →</Link>
              </div>
            ))
          )}
      </div>
    </>
  );
}

function SevCard({ level, title, body }: { level: 1 | 2 | 3; title: string; body: string }) {
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "11px 13px", background: "var(--subtle)", border: "1px solid var(--line)", borderRadius: 11 }}>
      <Sev level={level} />
      <div>
        <div style={{ fontSize: 12.5, fontWeight: 800 }}>{title}</div>
        <div style={{ fontSize: 11.5, color: "var(--ink2)", lineHeight: 1.5, marginTop: 2 }}>{body}</div>
      </div>
    </div>
  );
}
